var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useSail } from ".";
/**
 * Fetches data of the given accounts.
 * @param keys Keys to fetch. Ensure that this is memoized or unlikely to change.
 *
 * @returns One of three types:
 * - Buffer -- the account was found
 * - null -- account not found or an error occurred while loading the account
 * - undefined -- account key not provided or not yet loaded
 */
export const useAccountsData = (keys) => {
    const { getCached, onCache, subscribe, fetchKeys } = useSail();
    // makes a datum from a public key
    const makeDatumFromKey = useCallback((k) => {
        if (k) {
            const accountInfo = getCached(k);
            if (accountInfo) {
                return {
                    accountId: k,
                    accountInfo,
                };
            }
            if (accountInfo === null) {
                // Cache hit but null entry in cache
                return null;
            }
        }
        return k === undefined ? undefined : null;
    }, [getCached]);
    const [data, setData] = useState(keys.map(makeDatumFromKey));
    const fetchAndSetKeys = useDebouncedCallback((fetchKeys, keys) => __awaiter(void 0, void 0, void 0, function* () {
        setData(yield fetchKeys(keys));
    }), 100);
    useEffect(() => {
        void (() => __awaiter(void 0, void 0, void 0, function* () {
            yield fetchAndSetKeys(fetchKeys, keys);
        }))();
    }, [keys, fetchAndSetKeys, fetchKeys]);
    // subscribe to account changes
    useEffect(() => {
        const allKeysUnsubscribe = keys
            .filter((k) => !!k)
            .map(subscribe);
        return () => {
            allKeysUnsubscribe.map((fn) => fn());
        };
    }, [keys, subscribe]);
    // refresh from the cache whenever the cache is updated
    useEffect(() => {
        return onCache((e) => {
            if (keys.find((key) => key === null || key === void 0 ? void 0 : key.equals(e.id))) {
                void fetchAndSetKeys(fetchKeys, keys);
            }
        });
    }, [keys, onCache, fetchAndSetKeys, fetchKeys]);
    // unload debounces when the component dismounts
    useEffect(() => {
        return () => {
            fetchAndSetKeys.cancel();
        };
    }, [fetchAndSetKeys]);
    return data;
};
//# sourceMappingURL=useAccountsData.js.map